import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { profilesApi, notificationsApi, favouritesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Heart, Plus, Sparkles, Star, LogOut, Eye, Edit, Trash2, Loader2, 
  Compass, Shield, Bell, MapPin, Bookmark
} from 'lucide-react';import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Toaster, toast } from 'sonner';

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [favourites, setFavourites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showFavs, setShowFavs] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [profilesRes, unreadRes] = await Promise.all([
        profilesApi.getAll(),
        notificationsApi.getUnreadCount().catch(() => ({ data: { count: 0 } }))
      ]);
      setProfiles(profilesRes.data);
      setUnreadCount(unreadRes.data.count);
    } catch (error) {
      console.error('Failed to load:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadFavourites = async () => {
    try {
      const { data } = await favouritesApi.getAll();
      setFavourites(data);
      setShowFavs(true);
    } catch { toast.error('Failed to load favourites'); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await profilesApi.delete(deleteId);
      setProfiles(profiles.filter(p => p.id !== deleteId));
      toast.success('Tribute deleted');
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(false); setDeleteId(null); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const tierLabel = { free: 'Free', personal: 'Personal', family: 'Family', legacy: 'Legacy', unlimited: 'Unlimited' };
  const tierLimit = { free: 1, personal: 2, family: 5, legacy: 10, unlimited: UNLIMITED_LIMIT };
  const userTier = user?.tier || 'free';
  const limit = tierLimit[userTier] || 1;
  const canCreate = profiles.length < limit;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Toaster position="top-right" richColors />
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-purple-100/40 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-pink-100/40 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#1a1a2e]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#7c3aed] to-[#db2777] flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Heart className="w-4 h-4 text-white" strokeWidth={2} fill="currentColor" />
            </div>
            <span className="text-xl font-bold text-[#1a1a2e]">iExtol</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/explore">
              <Button variant="ghost" size="sm" className="text-[#1a1a2e]/70 hover:text-[#1a1a2e] rounded-full" data-testid="explore-link">
                <Compass className="w-4 h-4 mr-1.5" />Explore
              </Button>
            </Link>
            <Button variant="ghost" size="sm" className="text-[#1a1a2e]/70 hover:text-[#1a1a2e] rounded-full relative"
              onClick={() => { loadFavourites(); }} data-testid="favourites-link">
              <Bookmark className="w-4 h-4 mr-1.5" />Saved
            </Button>
            <Link to="/notifications">
              <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="notifications-link">
                <Bell className="w-4 h-4 text-[#1a1a2e]/70" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#db2777] text-white text-[10px] rounded-full flex items-center justify-center">{unreadCount}</span>
                )}
              </Button>
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin">
                <Button variant="ghost" size="sm" className="text-[#1a1a2e]/70 rounded-full" data-testid="admin-link">
                  <Shield className="w-4 h-4 mr-1.5" />Admin
                </Button>
              </Link>
            )}
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-[#1a1a2e]/10">
              <span className="text-sm text-[#1a1a2e]/70 hidden sm:block" data-testid="user-name">{user?.name}</span>
              <Badge variant="outline" className="text-xs">{tierLabel[userTier]}</Badge>
              <Button variant="ghost" size="icon" className="rounded-full" onClick={handleLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4 text-[#1a1a2e]/50" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        {/* Favourites panel */}
        {showFavs && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#1a1a2e] flex items-center gap-2"><Bookmark className="w-5 h-5 text-[#7c3aed]" />Saved Tributes</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowFavs(false)} className="text-[#1a1a2e]/50 rounded-full">Close</Button>
            </div>
            {favourites.length === 0 ? (
              <p className="text-[#1a1a2e]/40 text-sm">No saved tributes yet. Explore and save tributes you find meaningful.</p>
            ) : (
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {favourites.map(p => (
                  <Link key={p.id} to={`/tribute/${p.id}`}>
                    <Card className="border-[#1a1a2e]/5 bg-white shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer">
                      <CardContent className="p-4">
                        <p className="font-semibold text-[#1a1a2e]">{p.person_name}</p>
                        {p.location_country && <p className="text-xs text-[#1a1a2e]/40 flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location_country}</p>}
                        <p className="text-xs text-[#1a1a2e]/50 mt-1 italic line-clamp-1">"{p.headline}"</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
            <hr className="border-[#1a1a2e]/5" />
          </div>
        )}

        {/* Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1a1a2e] tracking-tight" data-testid="dashboard-heading">Your Tributes</h1>
            <p className="text-[#1a1a2e]/50 mt-1">{profiles.length} of {limit === UNLIMITED_LIMIT ? 'unlimited' : limit} tributes used</p>
          </div>
          {canCreate ? (
            <Link to="/create">
              <Button className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] hover:from-[#6d28d9] hover:to-[#be185d] text-white rounded-full shadow-lg shadow-purple-500/20 px-6" data-testid="create-tribute-button">
                <Plus className="w-4 h-4 mr-2" />Create Tribute
              </Button>
            </Link>
          ) : (
            <div className="text-right">
              <Link to="/pricing">
                <Button className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full px-6 shadow-lg" data-testid="upgrade-button">
                  <Sparkles className="w-4 h-4 mr-2" />Upgrade to create more
                </Button>
              </Link>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-[#7c3aed] animate-spin" /></div>
        ) : profiles.length === 0 ? (
          <Card className="border-[#1a1a2e]/5 bg-white shadow-lg" data-testid="empty-state">
            <CardContent className="py-20 text-center">
              <Heart className="w-16 h-16 text-[#1a1a2e]/10 mx-auto mb-6" strokeWidth={1} />
              <h2 className="text-2xl font-bold text-[#1a1a2e] mb-2">Honour someone special</h2>
              <p className="text-[#1a1a2e]/50 mb-8 max-w-md mx-auto">Create a tribute to celebrate the quiet heroes who shaped your life.</p>
              <Link to="/create">
                <Button className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full px-8 py-6 shadow-lg" data-testid="create-first-tribute">
                  <Plus className="w-5 h-5 mr-2" />Create your first tribute
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tributes-grid">
            {profiles.map(profile => {
              const isLiving = profile.profile_type === 'living';
              return (
                <Card key={profile.id} className="border-[#1a1a2e]/5 bg-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden group" data-testid={`tribute-card-${profile.id}`}>
                  <CardContent className="p-0">
                    <div className="aspect-[16/10] bg-gradient-to-br from-purple-50 to-pink-50 relative">
                      {profile.photo_url ? (
                        <img src={profile.photo_url.startsWith('http') ? profile.photo_url : `${profile.photo_url}`} alt={profile.person_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-4xl font-bold text-[#1a1a2e]/15">{profile.person_name.charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                      <div className="absolute top-3 left-3">
                        <Badge className={`${isLiving ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-violet-50 text-violet-600 border-violet-200'} border rounded-full`}>
                          {isLiving ? <Sparkles className="w-3 h-3 mr-1" /> : <Star className="w-3 h-3 mr-1" />}
                          {isLiving ? 'Living' : 'Legacy'}
                        </Badge>
                      </div>
                      <div className="absolute top-3 right-3">
                        <Badge variant="outline" className="bg-white/90 text-[#1a1a2e]/60 text-xs">{profile.privacy_setting}</Badge>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-[#1a1a2e] mb-0.5">{profile.person_name}</h3>
                      {(profile.location_city || profile.location_country) && (
                        <p className="text-xs text-[#1a1a2e]/40 flex items-center gap-1 mb-1">
                          <MapPin className="w-3 h-3" />{[profile.location_city, profile.location_country].filter(Boolean).join(', ')}
                        </p>
                      )}
                      <p className="text-sm text-[#1a1a2e]/50 mb-1">{profile.relationship}</p>
                      <p className="text-sm text-[#1a1a2e]/60 italic line-clamp-2 mb-4">"{profile.headline}"</p>
                      <div className="flex gap-2">
                        <Link to={`/tribute/${profile.id}`} className="flex-1">
                          <Button variant="outline" size="sm" className="w-full border-[#1a1a2e]/10 rounded-full" data-testid={`view-profile-${profile.id}`}>
                            <Eye className="w-4 h-4 mr-1.5" />View
                          </Button>
                        </Link>
                        <Link to={`/edit/${profile.id}`} className="flex-1">
                          <Button size="sm" className="w-full bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full" data-testid={`edit-profile-${profile.id}`}>
                            <Edit className="w-4 h-4 mr-1.5" />Edit
                          </Button>
                        </Link>
                        <Button variant="ghost" size="icon" className="text-[#1a1a2e]/30 hover:text-red-500 hover:bg-red-50 rounded-full"
                          onClick={() => setDeleteId(profile.id)} data-testid={`delete-profile-${profile.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white border-[#1a1a2e]/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1a1a2e]">Delete this tribute?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#1a1a2e]/50">This will permanently delete the tribute and all its sections, extols, and contributions.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="confirm-delete">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
